import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { projectsCacheTags } from './projectsApiHelpers';
import { basicHeaders } from '../utils';
import { makePaginationUrl, parseObjectString } from '../../utils/utils';
import { PaginatedApiResponse, PaginationParams } from '../types/common';
import { ResponseOf } from '../../@types/api-utils';

export const providedTags = ['dashboard'];
export type Dashboard = {
  id: number;
  name: string;
  dashboard_type: string;
  data: {
    widgets: DashboardWidget[];
  };
  owner_id: number;
  is_public: boolean;
  is_default: boolean;
  is_editable: boolean;
};

export type DashboardWidget = {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  config: any;
};
export const dashboardsApi = createApi({
  reducerPath: 'dashboardsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_APP_PROJECTS_SERVICE}/`,
    prepareHeaders: basicHeaders,
  }),
  tagTypes: [projectsCacheTags.DASHBOARDS],
  endpoints: (builder) => ({
    getDashboards: builder.query<Dashboard[], PaginationParams | undefined>({
      query: (params) => {
        return makePaginationUrl('service/dashboard', params);
      },
      providesTags: (response) =>
        response?.length
          ? [
              ...response.map(({ id }) => ({
                type: projectsCacheTags.DASHBOARDS as const,
                id,
              })),
              projectsCacheTags.DASHBOARDS,
            ]
          : [projectsCacheTags.DASHBOARDS],
    }),

    getDashboardsPaginated: builder.query<PaginatedApiResponse<Dashboard>, PaginationParams>({
      query: (params) => {
        return makePaginationUrl('service/dashboard/paginated', params);
      },
      providesTags: (response) =>
        response?.results
          ? [
              ...response.results.map(({ id }) => ({
                type: projectsCacheTags.DASHBOARDS as const,
                id,
              })),
              projectsCacheTags.DASHBOARDS,
            ]
          : [projectsCacheTags.DASHBOARDS],
    }),

    getDashboard: builder.query<Dashboard, number>({
      query: (id) => `service/dashboard/${+id}`,
      providesTags: (result) => [{ type: projectsCacheTags.DASHBOARDS, id: result?.id }],
    }),
    createDashboard: builder.mutation<Partial<Dashboard>, Partial<Dashboard>>({
      query: (body) => ({
        url: `service/dashboard`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [projectsCacheTags.DASHBOARDS],
      async onQueryStarted(newDashboard, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            dashboardsApi.util.updateQueryData('getDashboard', data.id, (draft) => {
              Object.assign(draft, data);
            })
          );
        } catch (error) {
          console.error('Failed to update getDashboard cache:', error);
        }
      },
    }),

    updateDashboard: builder.mutation<Dashboard, Partial<Dashboard>>({
      query: (body) => ({
        url: `service/dashboard/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [projectsCacheTags.DASHBOARDS],
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          dashboardsApi.util.updateQueryData('getDashboard', data.id, (draft) => {
            Object.assign(draft, data);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteDashboard: builder.mutation<void, number>({
      query: (id) => ({
        url: `service/dashboard/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [projectsCacheTags.DASHBOARDS],
      onQueryStarted(id, { dispatch }) {
        try {
          console.log(id);
          const recentDashboards = parseObjectString(
            localStorage.getItem('recentDashboards') ?? '{}'
          );
          const filtered = recentDashboards.filter((f: any) => f.id != id);
          localStorage.setItem('recentDashboards', JSON.stringify(filtered));
        } catch (error) {
          console.log(error);
        }
      },
    }),
    getPresetDashboards: builder.query<ResponseOf<'/service/dashboard/presets'>, void>({
      query: () => `service/dashboard/presets`,
      providesTags: [projectsCacheTags.NEW_TEAMS, projectsCacheTags.NEW_TEAM_RESOURCES],
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useLazyGetDashboardQuery,
  useGetDashboardsQuery,
  useLazyGetDashboardsQuery,
  useLazyGetDashboardsPaginatedQuery,
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
  useDeleteDashboardMutation,
  useGetPresetDashboardsQuery,
} = dashboardsApi;
